import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { MultimediaViewerService } from '@npm.chapps.com/chapps.common.angular.library';
import { Subscription } from 'rxjs';
import { map, tap } from 'rxjs/internal/operators';
import { ApplicationConfig } from '../../../config/application.config';
import { BaseComponent } from '../../shared/components/base.component';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ActionStatusType, ActionType, IssueCategoryType, MultimediumType, StructureType } from '../../shared/enums/enums';
import { AddIssueRequest } from '../../shared/model/addIssueRequest.model';
import { AddJobRequest } from '../../shared/model/addJobRequest.model';
import { ElementCategoryGroup } from '../../shared/model/elementCategoryGroup.model';
import { IdAndLabel } from '../../shared/model/id-and-label';
import { StructureSummary } from '../../shared/model/structureSummary.model';
import { BuildingService } from '../../shared/service/building-service';
import { InterventionTeamService } from '../../shared/service/interventionTeamService';
import { IssueService } from '../../shared/service/issue-service';
import { StructureService } from '../../shared/service/structure-service';
import { Multimedium } from "@npm.chapps.com/chapps.common.angular.library/lib/components/multimedium/model/multimedium";
import {Address} from "../../shared/model/address";
import {Building} from "../../shared/model/building";

@Component({
  selector: 'app-issue-dialog',
  templateUrl: './issue-dialog.component.html',
  styleUrls: ['./issue-dialog.component.scss']
})
export class IssueDialogComponent extends BaseComponent implements OnInit, OnDestroy {

  public formGroup: FormGroup;
  public kindOfProblemLabel: string;
  public subscriptions: Subscription[] = [];
  public jobLabel: string;
  public buildings: Building[] = [];
  public levels: IdAndLabel[] = [];
  public locations: IdAndLabel[] = [];
  public actionTypes: IdAndLabel[] = [];
  public elementCategoryGroup: ElementCategoryGroup[] = [];
  public structureCategories: StructureSummary[] = [];
  public kindOfProblems: IdAndLabel[] = [];
  public executors: IdAndLabel[] = [];
  public jobStatuses: IdAndLabel[] = [];
  public buildingId: number
  public multimedia: Multimedium[] = [];

  public showAddJobButton: boolean = true;
  public areBuildingsLoading: boolean = false;
  public areLevelsLoading: boolean = false;
  public areLocationsLoading: boolean = false;
  public areElementsLoading: boolean = false;
  public showActionStatus: boolean = false;
  public isIssueCreating: boolean = false;

  public get buildingFormControl(): FormControl { return this.formGroup.get('buildingId') as FormControl; }
  public get levelFormControl(): FormControl { return this.formGroup.get('level') as FormControl; }
  public get locationFormControl(): FormControl { return this.formGroup.get('structureId') as FormControl; }
  public get locationElementFormControl(): FormControl { return this.formGroup.get('elementStructureId') as FormControl; }
  public get kindOfProblemFormControl(): FormControl { return this.formGroup.get('kindOfProblem') as FormControl; }
  public get observationFormControl(): FormControl { return this.formGroup.get('observation') as FormControl; }
  public get filesFormControl(): FormControl { return this.formGroup.get('files') as FormControl; }
  public get actionTypeFormControl(): FormControl { return this.formGroup.get('type') as FormControl; }
  public get executorFormControl(): FormControl { return this.formGroup.get('interventionTeamId') as FormControl; }
  public get actionStatusTypeFormControl(): FormControl { return this.formGroup.get('status') as FormControl; }

  constructor(
    public dialogRef: MatDialogRef<IssueDialogComponent>,
    private buildingService: BuildingService,
    private interventionTeamService: InterventionTeamService,
    private structureService: StructureService,
    private appConfig: ApplicationConfig,
    private translate: TranslateService,
    private issueService: IssueService,
    private dialog: MatDialog,
    private multimediaViewerService: MultimediaViewerService,
    @Inject(MAT_DIALOG_DATA) public buildingGuid: string) {
    super();
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.kindOfProblemLabel = IssueCategoryType.MAINTENANCE.toString();
    this.kindOfProblems = this.getKindOfProblems();
    this.actionTypes = this.getActionTypes();
    this.jobStatuses = this.getJobStatusTypes();
    this.jobLabel = "default"
    this.loadData();
    this.initForm();
    this.elementCategoryGroup = [];


    this.subscriptions.push(
      this.multimediaViewerService.delete$.subscribe((result) => {
        if(result){
          this.multimedia = this.multimedia.filter(mm => mm.id != result);
        }
      })
    );

  }

  private initForm(){
    this.formGroup = new FormGroup({
      'buildingId': new FormControl(this.buildingId ? this.buildingId : null, [Validators.required]),
      'level': new FormControl(null, [Validators.required]),
      'structureId': new FormControl(null, []),
      'elementStructureId': new FormControl(null, []),
      'kindOfProblem': new FormControl(null, [Validators.required]),
      'observation': new FormControl(null, []),
      'files': new FormControl([], [])
    });

    this.subscriptions.push(this.kindOfProblemFormControl.valueChanges.subscribe((v) => {
        v ? this.kindOfProblemLabel = v : this.kindOfProblemLabel = IssueCategoryType.MAINTENANCE.toString();
    }));

    this.subscriptions.push(this.buildingFormControl.valueChanges.subscribe((v) => {
      if(v){
        this.loadBuildingStructures(v);
      }
    }));

    this.subscriptions.push(this.levelFormControl.valueChanges.subscribe((v) => {
      if(v){
        this.loadLocations(v);
      }
    }));

    this.subscriptions.push(this.locationFormControl.valueChanges.subscribe((v) => {
      if(v){
        this.loadLocationElements(v);
      }
    }));
  }



  public cancel(){
    const confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(this.Localization.LoseUnsavedChanges),
        text: this.translate.instant(this.Localization.ExitConfirmation)
      },
      autoFocus: false
    });
    confirmationDialogRef.componentInstance.closed.subscribe((confirmed) => {
      if(confirmed){
        confirmationDialogRef.close();
        this.close(false);
      }

    })
  }

  public close(saved: boolean){
    this.formGroup.reset();
    this.dialogRef.close(saved);
  }

  public onSubmit(){
    if(this.formGroup.invalid){
      Object.values(this.formGroup.controls)
        .forEach(c => c.markAsTouched());
    }else{
      this.createIssue();
    }
  }

  private createIssue(){
    this.isIssueCreating = true;
    this.issueService.createIssue(this.convertFormModelToIssue())
      .subscribe(
        () => {
          this.isIssueCreating = false;
          this.close(true);
        }, () => this.isIssueCreating = false, () => this.isIssueCreating = false
      )
  }

  private convertFormModelToIssue(): AddIssueRequest{
    const building = this.buildings.find(b => b.id == this.buildingFormControl.value);
    let issueRequest: AddIssueRequest = {
      buildingId: building.guid,
      structureId: this.locationFormControl.value,
      elementStructureId: this.locationElementFormControl.value,
      observation: this.observationFormControl.value,
      kindOfProblem: this.kindOfProblemFormControl.value,
      files: this.filesFormControl.value,
      createdOn: new Date(),
    };
    if (!this.showAddJobButton) {
      let jobRequest: AddJobRequest = {
        interventionTeamId: this.executorFormControl && this.executorFormControl.value != this.translate.instant(this.Localization.ToBeAssigned) ? this.executorFormControl.value : null,
        type: this.actionTypeFormControl ? this.actionTypeFormControl.value : null,
        status: this.actionStatusTypeFormControl ? this.actionStatusTypeFormControl.value : ActionStatusType.OPEN,
      };
      issueRequest.job = jobRequest;
    }

    return issueRequest;
  }


  public onFileSelected(event: any) {
    const supportedImageFiles = this.filterFiles(event.target.files, this.appConfig.SUPPORTED_IMAGE_FILE_EXTENSIONS);
    var reader = new FileReader();
    supportedImageFiles.forEach((image: File) => {
      this.filesFormControl.value.push(image);
      this.filesFormControl.updateValueAndValidity();
      reader.readAsDataURL(image);
      reader.onload = this.handleReaderLoader.bind(this);
      reader.onloadend = function() {
        event.target.value = "";
      }
    });
  }

  private handleReaderLoader(e: any){
    var multimedium = {
      id: this.multimedia.length +1,
      url: e.target.result,
      type: MultimediumType.PICTURE,
    } as Multimedium
    this.multimedia.push(multimedium);
  }

  public openMediaInMultimediaViewer(index: number){
    this.multimediaViewerService.show(this.multimedia, index);
  }

  private filterFiles(files: FileList, supportedFileExtensions: string[]): File[] {
    const result = [];
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      const fileExt = file.name.split('.').pop().toLowerCase();
      if (supportedFileExtensions.find(ext => ext === fileExt)) {
        result.push(file);
      }
    }
    return result;
  }

  public onActionTypeChange($event: MatSelectChange){
    $event.value ? this.jobLabel = $event.value: this.jobLabel = "Default";
  }

  public onExecutorChange($event: MatSelectChange){
    if($event.value != this.translate.instant(this.Localization.ToBeAssigned)){
      this.showActionStatus = true;
      this.formGroup.addControl('status', new FormControl(null, [Validators.required]))
    }else{
      this.showActionStatus = false;
      if(this.actionStatusTypeFormControl){
        this.formGroup.removeControl('status');
      }
    }
  }

  public addJob(){
    this.showAddJobButton = !this.showAddJobButton;
    if(!this.showAddJobButton){
      this.formGroup.addControl('type', new FormControl(null, []));
      this.formGroup.addControl('interventionTeamId', new FormControl(null, [Validators.required]));
    }else{
      this.executors = [];
      this.formGroup.removeControl('type');
      this.formGroup.removeControl('interventionTeamId');
      this.formGroup.removeControl('status');
    }
    this.formGroup.updateValueAndValidity();
  }

  public formatAddress(address: Address): string {
    return [
      [address.street, address.houseNumber, address.houseNumberExtenstion, address.mailBox].join(' '),
      [address.zipCode, address.city].join(' ')
    ].join(', ');
  }

  private loadData(){
    this.loadBuildings();
  }

  private loadBuildings(){
    this.buildings = [];
    this.areBuildingsLoading = true;
    this.subscriptions.push(this.interventionTeamService.selectedInterventionTeam.subscribe(team => {
      this.executors.push(new IdAndLabel(team.id, team.label));
      this.buildingService.getBuildings()
        .subscribe(response => {
          this.buildings = response;
          this.buildingId = this.buildings.find(b => { return b.guid == this.buildingGuid })?.id;
          this.buildingFormControl.setValue(this.buildingId);
        })
    }));
  }

  private loadBuildingStructures(buildingId: number){
    this.levels = [];
    this.areLevelsLoading = true;
    this.structureService.getStructures(buildingId)
      .pipe(
        map(structures => structures.map(s => new IdAndLabel(s.id, s.label))),
        tap(() => this.areLevelsLoading = false)
      )
      .subscribe(response => {
        this.levels = response;
      })
  }

  private loadLocations(structureId: number){
    this.areLocationsLoading = true;
    this.structureService.getStructures(structureId)
      .subscribe((result) => {
        const privateUnits = result.find(area => area.type === StructureType.PRIVATEUNITS);
        const commonAreas = result.filter(area => area.type != StructureType.PRIVATEUNITS);
        this.loadUnits(privateUnits?.id, commonAreas);
        this.areLocationsLoading = false;
      })
  }

  private loadUnits(privateUnitsId: number, commonAreas?: StructureSummary[]){
    this.locations = [];
    this.locations = commonAreas;
    if(privateUnitsId){
      this.structureService.getStructures(privateUnitsId).subscribe(
        (result) => {
          this.locations = this.locations.concat(result.sort((a, b) => a.index - b.index));
        },
        (err) => {
          this.areLocationsLoading = false;
        }
      );
    }else {
      this.areLocationsLoading = false;
    }
  }

  private loadLocationElements(locationId: number){
    this.areElementsLoading = true;
    this.structureService.getLocationElements(locationId).subscribe((result) => {
      let elements = result
        .filter(e => this.appConfig.STRUCTURE_ELEMENT_TYPES.find(type => type === e.type))
        .map(e => ({
          id: e.id,
          label: e.label,
          category: this.translate.instant(`structureType_${e.categoryType}`) as string,
        }));
    elements.forEach(el => {
      if(this.elementCategoryGroup.some(ecg => ecg.category == el.category.toString())){
        this.elementCategoryGroup.find(ecg => ecg.category == el.category.toString()).elements.push({
          id: el.id,
          label: el.label
        });
      }else{
        this.elementCategoryGroup.push({
          category: el.category,
          elements: [
            {
              id: el.id,
              label: el.label
            }
          ]
        })
      }
    })
    }, () => this.areElementsLoading = false, () => this.areElementsLoading = false);
  }

  private getKindOfProblems() {
    return Object.keys(IssueCategoryType).map(category => new IdAndLabel(IssueCategoryType[category],
      this.translate.instant(`issueCategoryType_${IssueCategoryType[category]}`)));
  }

  private getActionTypes(){
    return Object.keys(ActionType).map(type => new IdAndLabel(ActionType[type], this.translate.instant(`actionType_${ActionType[type]}`)));
  }

  private getJobStatusTypes(){
    return Object.keys(ActionStatusType).map(type => new IdAndLabel(ActionStatusType[type], this.translate.instant(`actionStatusType_${ActionStatusType[type]}`))).filter(type => type.id == ActionStatusType.EXECUTING || type.id == ActionStatusType.FINISHED || type.id == ActionStatusType.OPEN)
  }
}
